import { ToofiInfoCue } from "@/components/ui/reader-ai/toofi-info-cue";
import { TOOFI_META_CHIP_CLASS } from "@/lib/toofi-ux-tokens";
import { cn } from "@/lib/utils";
import type { ReaderAiMemoryUsage, ReaderAiSourceOrigin } from "@/types/agentic-chat";
import { Workflow } from "lucide-react";

const sourceOriginLabel: Record<ReaderAiSourceOrigin, string> = {
  pdf: "PDF",
  general_knowledge: "General",
  memory: "Profile",
  mixed: "PDF + memory",
};

const sourceOriginTone: Record<ReaderAiSourceOrigin, string> = {
  pdf: "border-blue-100/70 bg-blue-50/55 text-blue-700",
  general_knowledge: "border-slate-200/70 bg-white/65 text-slate-600",
  memory: "border-emerald-100/70 bg-emerald-50/55 text-emerald-700",
  mixed: "border-slate-200/70 bg-white/65 text-slate-600",
};

const usesAnyMemory = (usage?: ReaderAiMemoryUsage) => {
  if (!usage) return false;
  return [usage.usedProfile, usage.usedCourseMemory, usage.usedDocumentMemory, usage.usedSessionMemory].some(Boolean);
};

type ReaderAiSourceRibbonProps = {
  sourceOrigin?: ReaderAiSourceOrigin;
  memoryUsage?: ReaderAiMemoryUsage;
  compact?: boolean;
};

export function ReaderAiSourceRibbon({ sourceOrigin, memoryUsage, compact = false }: ReaderAiSourceRibbonProps) {
  if (!sourceOrigin && !memoryUsage) return null;
  const hasMemoryUsage = usesAnyMemory(memoryUsage);

  return (
    <div className={cn("mb-1 flex flex-wrap items-center gap-1.5 lg:mb-1.5", compact ? "mb-1 gap-1" : "lg:gap-2")}>
      {sourceOrigin ? (
        <span
          title={sourceOriginLabel[sourceOrigin]}
          className={cn(
            TOOFI_META_CHIP_CLASS,
            sourceOriginTone[sourceOrigin],
            compact && "gap-1 px-2 py-0.5 text-[9px]",
          )}
        >
          <span>{sourceOriginLabel[sourceOrigin]}</span>
          <ToofiInfoCue label="Source info" explanation={sourceOriginLabel[sourceOrigin] === "PDF" ? "Grounded in your current PDF." : sourceOriginLabel[sourceOrigin] === "General" ? "Uses general knowledge, not only the PDF." : sourceOriginLabel[sourceOrigin] === "Profile" ? "Uses your saved study preferences or patterns." : "Mixes your PDF with saved study context."} />
        </span>
      ) : null}
      {hasMemoryUsage ? (
        <span
          title="This reply used your saved study context"
          className={cn(TOOFI_META_CHIP_CLASS, compact && "gap-1 px-2 py-0.5 text-[9px]")}
        >
          <Workflow className={cn("h-3 w-3 opacity-75", compact && "h-2.5 w-2.5")} />
          <span>Uses memory</span>
          <ToofiInfoCue label="Memory info" explanation="This answer used your saved study context, not just the current thread." />
        </span>
      ) : null}
    </div>
  );
}
